/**
 * components/ErrorBoundary
 * ──────────────────────────────────────────────────────────────────────────
 * The last line of defence. A render crash anywhere below this shows a page a
 * person can act on instead of a white screen.
 *
 * Deliberately a CLASS: `componentDidCatch` has no hook equivalent, and this is
 * the one place in the codebase where that matters.
 *
 * It shows the error message. In a cash subledger that is the right trade: a
 * finance user who can read "GET /v1/overview returned an unexpected shape at
 * kpis.cash_in_custody" can tell support something useful, where "Something
 * went wrong" wastes a support round trip. No stack trace, and nothing from the
 * request body — only the message.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { sans, mono, size as fontSize, leading, type as textRole } from '../../theme/typography';
import Button from '../Button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the only sink available here. A crash reporter, when the
    // suite adopts one, hooks in at this single point.
    console.error('[SK Company Cash] render error', error.message, info.componentStack);
  }

  private readonly reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <View style={styles.wrap}>
        <Text style={textRole.pageTitle}>This screen could not be drawn</Text>
        <Text style={styles.blurb}>
          Nothing was changed. Your cash records are unaffected — this console only reads and
          records, it never moves money.
        </Text>
        <Text style={styles.detail}>{error.message}</Text>
        <Button label="Try again" size="xl" variant="primary" onPress={this.reset} />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 32,
    backgroundColor: neutral.surface,
  },
  blurb: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.grey600,
    textAlign: 'center',
    maxWidth: 460,
  },
  detail: {
    ...mono('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey400,
    textAlign: 'center',
    maxWidth: 520,
  },
});

export default ErrorBoundary;
